import type { ICustomControlData } from './SpirometryAddEdit';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { translateKey } from '../../../../../translate';
import { FormDivider, FormParagraph, HeaderDivider, TriStateWithTextShow } from '../../../../elements/common';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { NullableBoolean } from '../../../../elements/NullableBoolean';
import { NullableFormattedDate } from '../../../../elements/NullableFormattedDate';

interface ISpirometryReadProps {
    customControlData: ICustomControlData;
    pulmoType: 'procedure' | 'request';
}

export function SpirometryRead(props: ISpirometryReadProps) {
    return (
        <>
            {props.pulmoType === 'procedure' && (
                <>
                    <HorizontalFormGroupRead>
                        <FormLabel>KetenDCB COPD/Astma?</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                <NullableBoolean value={props.customControlData.keten_DBC_COPD_astma} />
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    <HorizontalFormGroupRead>
                        <FormLabel>Spirogram incl. reversibiliteitstest?</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                <NullableBoolean value={props.customControlData.spirogram_inc_reversibiliteitstest} />
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    <HorizontalFormGroupRead>
                        <FormLabel>Datum functieonderzoek</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.customControlData.datum_afname_functieonderzoek !== null && (
                                    <NullableFormattedDate
                                        value={props.customControlData.datum_afname_functieonderzoek}
                                    />
                                )}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    <FormDivider />
                </>
            )}
            {props.customControlData.lengte !== null && props.customControlData.gewicht !== null && (
                <>
                    <HeaderDivider text={translateKey('request.headerMeasurement')} />

                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.weight')}</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.customControlData.gewicht}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.length')}</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.customControlData.lengte}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    <HorizontalFormGroupRead>
                        <FormLabel>BMI</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {calculateBmi(props.customControlData.gewicht, props.customControlData.lengte)}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                </>
            )}

            {(props.customControlData.klachten ||
                props.customControlData.MRC ||
                props.customControlData.CCQ ||
                props.customControlData.ACQ) && (
                <>
                    <FormDivider />
                    <HeaderDivider text="Anamnese" />
                </>
            )}

            <FlexColumns sm={12}>
                {props.customControlData.klachten ? (
                    <>
                        {(props.customControlData.klachten.bekend_met_astma_value !== null ||
                            props.customControlData.klachten.bekend_met_astma_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Bekend met astma</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.bekend_met_astma_text}
                                            value={props.customControlData.klachten.bekend_met_astma_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.astma_in_de_familie_value !== null ||
                            props.customControlData.klachten.astma_in_de_familie_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Astma in de familie</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.astma_in_de_familie_text}
                                            value={props.customControlData.klachten.astma_in_de_familie_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.bekend_met_hartfalen_value !== null ||
                            props.customControlData.klachten.bekend_met_hartfalen_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Bekend met hartfalen</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.bekend_met_hartfalen_text}
                                            value={props.customControlData.klachten.bekend_met_hartfalen_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.slijm_opgeven_value !== null ||
                            props.customControlData.klachten.slijm_opgeven_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Slijm opgeven</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.slijm_opgeven_text}
                                            value={props.customControlData.klachten.slijm_opgeven_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.kortademig_s_nachts_value !== null ||
                            props.customControlData.klachten.kortademig_s_nachts_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Kortademig 's nachts</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.kortademig_s_nachts_text}
                                            value={props.customControlData.klachten.kortademig_s_nachts_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.allergie_value !== null ||
                            props.customControlData.klachten.allergie_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Allergie</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.allergie_text}
                                            value={props.customControlData.klachten.allergie_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.bekend_met_COPD_value !== null ||
                            props.customControlData.klachten.bekend_met_COPD_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Bekend met COPD</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.bekend_met_COPD_text}
                                            value={props.customControlData.klachten.bekend_met_COPD_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.hyperreactiviteit_value !== null ||
                            props.customControlData.klachten.hyperreactiviteit_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Hyperreactiviteit</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.hyperreactiviteit_text}
                                            value={props.customControlData.klachten.hyperreactiviteit_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.maagklachten_value !== null ||
                            props.customControlData.klachten.maagklachten_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Maagklachten</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.maagklachten_text}
                                            value={props.customControlData.klachten.maagklachten_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {(props.customControlData.klachten.KNO_klachten_value !== null ||
                            props.customControlData.klachten.KNO_klachten_text !== null) && (
                            <HorizontalFormGroupRead>
                                <FormLabel>KNO klachten</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        <TriStateWithTextShow
                                            text={props.customControlData.klachten.KNO_klachten_text}
                                            value={props.customControlData.klachten.KNO_klachten_value}
                                        />
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.klachten.rookgedrag !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Roker</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.klachten.rookgedrag}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.klachten.beroep !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Beroep</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.klachten.beroep}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.customControlData.klachten.aantal_exacerbaties_in_het_afgelopen_jaar !== null && (
                            <HorizontalFormGroupRead>
                                <FormLabel>Aantal exacerbaties in het afgelopen jaar</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        {props.customControlData.klachten.aantal_exacerbaties_in_het_afgelopen_jaar}
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}
                    </>
                ) : null}
                <>
                    {props.customControlData.MRC && (
                        <>
                            <HorizontalFormGroupRead>
                                <FormLabel>MRC-score</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.MRC.scoreMrc}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        </>
                    )}
                    {props.customControlData.CCQ && (
                        <>
                            <HorizontalFormGroupRead>
                                <FormLabel>CCQ-score</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.CCQ.scoreCcqTotal}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                            <HorizontalFormGroupRead>
                                <FormLabel>CCQ-score: Klachten</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.CCQ.scoreCcqComplaints}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                            <HorizontalFormGroupRead>
                                <FormLabel>CCQ-score: Emoties</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.CCQ.scoreCcqEmotions}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                            <HorizontalFormGroupRead>
                                <FormLabel>CCQ-score: Conditie</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.CCQ.scoreCcqCondition}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        </>
                    )}
                    {props.customControlData.ACQ && (
                        <>
                            <HorizontalFormGroupRead>
                                <FormLabel>ACQ-score</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.customControlData.ACQ.scoreAcq}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                            {props.customControlData.ACQ.FEV1_voor_luchtwegverwijding && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>FEV1 voor luchtwegverwijding</FormLabel>
                                    <InputContainer>
                                        <FormParagraph>
                                            {props.customControlData.ACQ.FEV1_voor_luchtwegverwijding}
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}
                            {props.customControlData.ACQ.FEV1_voorspeld && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>FEV1 voorspeld</FormLabel>
                                    <InputContainer>
                                        <FormParagraph>{props.customControlData.ACQ.FEV1_voorspeld}</FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}
                        </>
                    )}
                </>
            </FlexColumns>

            {(props.customControlData.klachten ||
                props.customControlData.MRC ||
                props.customControlData.CCQ ||
                props.customControlData.ACQ) && <FormDivider />}
        </>
    );
}
